import React, { useState, useEffect } from 'react';
import { detectMobileOS, APP_STORE_LINKS } from '../../../config/appLinks';

const BANNER_STORAGE_KEY = 'fixome_app_banner_dismissed_until';

/**
 * Mobile Smart App Banner
 * Appears prominently at the top on mobile devices to route users to the App Store or Google Play
 */
const MobileSmartAppBanner = () => {
  // Always paint the 48px bar. An inline head script adds
  // `fx-app-banner-dismissed` before first paint when it should stay hidden,
  // so prerendered HTML and the client tree match and the bar does not pop in.
  const [isVisible, setIsVisible] = useState(true);
  const [os, setOs] = useState('other');

  useEffect(() => {
    setOs(detectMobileOS());

    try {
      const dismissedUntil = localStorage.getItem(BANNER_STORAGE_KEY);
      if (dismissedUntil && Number(dismissedUntil) > Date.now()) {
        setIsVisible(false);
        return;
      }
    } catch {
      // Ignore localStorage errors
    }

    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    if (isStandalone) {
      setIsVisible(false);
    }
  }, []);

  const handleDismiss = () => {
    setIsVisible(false);
    document.documentElement.classList.add('fx-app-banner-dismissed');
    try {
      // Dismiss for 7 days
      const expireTime = Date.now() + 7 * 24 * 60 * 60 * 1000;
      localStorage.setItem(BANNER_STORAGE_KEY, expireTime.toString());
    } catch {
      // Ignore localStorage errors
    }
  };

  if (!isVisible) return null;

  const targetLink =
    os === 'ios'
      ? APP_STORE_LINKS.APPLE_APP_STORE
      : APP_STORE_LINKS.GOOGLE_PLAY_STORE;

  const buttonText = os === 'ios' ? 'GET' : 'INSTALL';

  return (
    <aside
      aria-label="Download Fixome Mobile App"
      data-fx-app-banner=""
      className="bg-slate-900 text-white border-b border-slate-800 relative z-40 h-12 max-h-12 md:h-10 md:max-h-10 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto h-12 md:h-10 px-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dismiss app download banner"
            className="text-slate-400 hover:text-white h-12 w-12 md:h-8 md:w-8 inline-flex items-center justify-center rounded-full flex-shrink-0"
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <span className="font-semibold text-base md:text-xs text-white truncate">
            Fixome app
          </span>
        </div>
        <a
          href={targetLink}
          target="_blank"
          rel="noopener noreferrer"
          className="border border-white/40 text-white font-semibold text-xs md:text-[11px] leading-none px-3 md:px-2.5 h-12 md:h-7 min-w-12 md:min-w-0 inline-flex items-center rounded-full flex-shrink-0"
        >
          {buttonText}
        </a>
      </div>
    </aside>
  );
};

export default MobileSmartAppBanner;
